import { Link } from '@remix-run/react';
import { Menu } from 'lucide-react';
import { createContext, ReactNode, useContext, useState } from 'react';
import logo from '~/assets/Logo.svg';
import useTranslation from '~/i18n/i18n';
import { useUser } from '~/lib/providers/user-provider';
import { ThemePicker } from '../common/theme-toggle';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import { Avatar } from './avatar';
import { Links } from './links';

interface MobileMenuType {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const MobileMenuContext = createContext<MobileMenuType | undefined>(undefined);

export const MobileMenuProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <MobileMenuContext.Provider value={{ open, setOpen }}>
      {children}
    </MobileMenuContext.Provider>
  );
};

// Hook to access the game access code
export function useMobileMenu() {
  const context = useContext(MobileMenuContext);
  if (!context) {
    throw new Error('useMobileMenu must be used within a MobileMenuProvider');
  }
  return { open: context.open, setOpen: context.setOpen };
}

export const Navbar = () => {
  const { t } = useTranslation();
  const { user } = useUser();

  const { open, setOpen } = useMobileMenu();
  return (
    <nav className="flex h-fit w-full flex-row items-center justify-between gap-4">
      <div className="flex grow flex-row items-center justify-start md:gap-16">
        {/* If the user is authenticated, redirect to the dashboard, otherwise redirect to the landing page. */}
        <Link to={user ? '/dashboard' : '/'} className="shrink-0">
          <img
            src={logo}
            alt={t('common.appName')}
            className="h-12 w-auto dark:invert md:h-14 md:w-auto"
          />
        </Link>

        <Links className="hidden flex-1 lg:block" />
      </div>

      <div className="scale-90">
        <ThemePicker />
      </div>

      {user && <Avatar className="hidden lg:flex" />}

      {/* Mobile menu */}
      <div className="block h-full lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger>
            <Menu className="h-6 w-6 text-foreground" />
          </SheetTrigger>
          <SheetContent
            fullscreen={true}
            aria-describedby={t('common.appName')}>
            <SheetHeader className="hidden">
              <SheetTitle>{t('common.appName')}</SheetTitle>
              <SheetDescription>{t('common.appName')}</SheetDescription>
            </SheetHeader>
            <div className="flex h-full flex-col items-start justify-center gap-6">
              <div className="flex w-full grow flex-col gap-6">
                <div className="flex w-full items-start justify-start pb-6">
                  <Link to="/dashboard" onClick={() => setOpen(false)}>
                    <img
                      src={logo}
                      alt={t('common.appName')}
                      className="w-2/3 dark:invert"
                    />
                  </Link>
                </div>
                <Links className="space-x-2 font-special text-5xl" />
              </div>
              {user && <Avatar mobile />}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};
